import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Sparkles, FileSearch, Languages, MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MarkdownContent } from "@/components/MarkdownContent";

export default function Documents() {
  const { t } = useI18n();
  const { refreshUser } = useAuth();
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [question, setQuestion] = useState("");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); toast.success(t("common.success")); }
    catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openDoc = async (id) => { const { data } = await api.get(`/documents/${id}`); setSelected(data); };

  const analyze = async (kind) => {
    if (!selected) return;
    if (kind === "qa" && !question.trim()) { toast.error(t("documents.question")); return; }
    setAnalyzing(true);
    try {
      await api.post(`/documents/${selected.id}/analyze`, { kind, question, target_lang: "en" });
      await openDoc(selected.id); setQuestion(""); refreshUser();
    } catch (err) { toast.error(err.response?.status === 402 ? err.response.data?.detail?.message : (err.response?.data?.detail || t("common.error"))); }
    finally { setAnalyzing(false); }
  };

  const del = async (id) => { await api.delete(`/documents/${id}`); if (selected?.id === id) setSelected(null); load(); };

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title={t("documents.title")} subtitle={t("documents.subtitle")}
        actions={<Button className="rounded-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn">
          <Upload className="h-4 w-4 mr-2" />{uploading ? t("common.loading") : t("documents.upload")}</Button>} />
      <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={upload} data-testid="file-input" />

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-display font-semibold mb-3">{t("documents.myDocuments")}</h3>
          <p className="text-xs text-muted-foreground mb-3">{t("documents.dropHint")}</p>
          {docs.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-documents">{t("documents.noDocuments")}</p>}
          <div className="space-y-2">
            {docs.map((d) => (
              <button key={d.id} onClick={() => openDoc(d.id)} data-testid="document-card"
                className={`w-full text-left rounded-xl border bg-card p-3 flex items-center justify-between transition-colors ${selected?.id === d.id ? "border-primary" : "hover:bg-muted"}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <div className="min-w-0"><div className="font-medium text-sm truncate">{d.filename}</div>
                    <div className="text-xs text-muted-foreground">{d.size < 1024 ? `${d.size} octets` : `${(d.size / 1024).toFixed(1)} Ko`} {!d.has_text && "· texte non extrait"}</div></div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(d.id); }} data-testid="delete-document"><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
              </button>
            ))}
          </div>
        </div>

        <div>
          {!selected ? <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">{t("documents.myDocuments")}</div> : (
            <div className="space-y-4">
              <div className="rounded-2xl border bg-card p-5">
                <div className="font-medium mb-3">{selected.filename}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => analyze("summary")} disabled={analyzing} data-testid="doc-summarize"><Sparkles className="h-4 w-4 mr-1.5" />{t("documents.summarize")}</Button>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => analyze("analysis")} disabled={analyzing} data-testid="doc-analyze"><FileSearch className="h-4 w-4 mr-1.5" />{t("documents.analyze")}</Button>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => analyze("translation")} disabled={analyzing} data-testid="doc-translate"><Languages className="h-4 w-4 mr-1.5" />{t("documents.translateDoc")}</Button>
                </div>
                <div className="flex gap-2 mt-3">
                  <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t("documents.question")} data-testid="doc-question" />
                  <Button size="sm" className="rounded-full shrink-0" onClick={() => analyze("qa")} disabled={analyzing} data-testid="doc-ask"><MessageCircleQuestion className="h-4 w-4" /></Button>
                </div>
              </div>
              <div>
                <h4 className="font-display font-semibold mb-2">{t("documents.analyses")}</h4>
                <div className="space-y-2">
                  {selected.analyses?.map((a) => (
                    <div key={a.id} className="rounded-xl border bg-card p-4" data-testid="analysis-card">
                      <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-1">{a.kind}{a.question && `: ${a.question}`}</div>
                      <MarkdownContent content={a.result} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
